import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Wraps a (flattened, borderless) input in a single bordered field with a
 * fixed-width suffix segment on the right, reading as one control: "[ 12.5 │ lbs ]".
 *
 * The suffix segment is always reserved at the same width, so the input area is
 * identical across every row whether or not a field has a suffix. When a suffix is
 * present the segment is styled as an attached addon (divider + subtle tint) so
 * it carries enough weight to sit next to the input; when absent it's blank,
 * leaving empty space rather than widening the input.
 * @param suffix - The label shown in the right segment, if any
 * @param children - The input control rendered on the left
 * @returns The combined input-with-suffix control
 */
export function InputWithSuffix({
  suffix,
  children,
}: {
  suffix?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex h-8 items-stretch overflow-hidden rounded-md border border-input bg-transparent shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring">
      <div className="flex min-w-0 flex-1">{children}</div>
      <div
        className={cn(
          "flex w-16 shrink-0 items-center justify-center whitespace-nowrap px-2 text-xs text-muted-foreground",
          suffix && "border-l border-input bg-muted/50",
        )}
      >
        {suffix}
      </div>
    </div>
  );
}
